import { useState } from 'react';
import { createPortal } from 'react-dom';
import logoIcon from '../../assets/icons/MeinGruenraum_Logo.png';
import {
  clearDeferredPrompt,
  getDeferredPrompt,
  isIOS,
  isIOSSafari,
} from '../../lib/installPrompt';

const DISMISSED_KEY = 'mgr_install_dismissed';

/**
 * InstallPromptSheet — PWA install bottom sheet.
 *
 * Two views:
 *  - "prompt"       : initial offer with Später / Zum Homescreen
 *  - "ios-guide"    : visual instructions for iOS (no programmatic install API)
 *
 * Whether an installation is possible at all is decided *before* this sheet is
 * mounted, by InstallPromptGuard via `canInstall()` (#680). The sheet therefore
 * no longer listens for `beforeinstallprompt` itself — it reads the captured
 * event at click time from the shared module, which is also the only place that
 * can have seen an event fired before React mounted.
 */
const InstallPromptSheet = ({ onDismiss }) => {
  const [view, setView] = useState('prompt');

  const dismiss = ({ permanent = false } = {}) => {
    // A timestamp lets InstallPromptGuard re-offer after the cooldown (#488);
    // 'true' suppresses for good and is reserved for cases where offering again
    // would be pointless (installed, or this browser simply cannot install).
    localStorage.setItem(DISMISSED_KEY, permanent ? 'true' : String(Date.now()));
    onDismiss();
  };

  const handleLater = () => {
    dismiss();
  };

  const handleInstall = async () => {
    const deferredPrompt = getDeferredPrompt();
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      // The event is single-use — drop it so canInstall() reflects reality.
      clearDeferredPrompt();
      if (outcome === 'accepted') {
        // Installed → permanent dismiss ('true', never re-prompt), distinct from
        // the timestamp-based cooldown that dismiss() writes (#488).
        localStorage.setItem(DISMISSED_KEY, 'true');
      }
      onDismiss();
      return;
    }
    if (isIOS()) {
      setView('ios-guide');
      return;
    }
    // No native prompt and not iOS — this browser cannot install (Desktop
    // Firefox/Safari). The guard normally keeps us from ever getting here; if we
    // do (e.g. the event was consumed elsewhere), suppress for good rather than
    // re-offering the same dead end every cooldown (#680).
    dismiss({ permanent: true });
  };

  const renderPromptView = () => (
    <>
      <div className="flex gap-3.5 items-start mb-4">
        <img
          src={logoIcon}
          alt="Mein Grünraum"
          className="w-9 h-9 object-contain flex-none"
        />
        <div>
          <h3 className="text-base font-bold text-gray-900 m-0">
            Grünraum als App speichern
          </h3>
          <p className="text-[13px] text-gray-600 mt-1 leading-relaxed m-0">
            Öffne Grünraum direkt vom Homescreen — ohne Browser-Leiste.
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleLater}
          className="flex-1 bg-white border border-gray-300 rounded-lg py-3 text-[13px] font-medium text-gray-700 min-h-[44px] hover:bg-gray-50 transition-colors cursor-pointer"
        >
          Später
        </button>
        <button
          onClick={handleInstall}
          className="flex-[2] bg-emerald-500 text-white rounded-lg py-3 text-[13px] font-medium min-h-[44px] hover:bg-emerald-600 transition-colors cursor-pointer"
        >
          Zum Homescreen
        </button>
      </div>
    </>
  );

  const renderIOSGuide = () => {
    const safari = isIOSSafari();
    return (
      <>
        <div className="flex gap-3.5 items-start mb-4">
          <img
            src={logoIcon}
            alt="Mein Grünraum"
            className="w-9 h-9 object-contain flex-none"
          />
          <div>
            <h3 className="text-base font-bold text-gray-900 m-0">
              Zum Home-Bildschirm hinzufügen
            </h3>
            <p className="text-[13px] text-gray-600 mt-1 leading-relaxed m-0">
              {safari
                ? 'In drei Schritten landet Grünraum als App auf deinem Home-Bildschirm.'
                : 'Auf iPhone und iPad funktioniert das Hinzufügen nur in Safari.'}
            </p>
          </div>
        </div>

        {!safari && (
          <div
            className="rounded-lg p-3 mb-4 text-[13px] leading-relaxed"
            style={{ background: '#FEF3C7', color: '#92400E' }}
            role="note"
          >
            <strong className="font-semibold">Hinweis:</strong> Andere Browser
            auf iOS (Chrome, Firefox, Edge) können keine Apps installieren.
            Öffne diese Seite bitte in Safari und versuche es dort erneut.
          </div>
        )}

        <ol className="text-[13px] text-gray-700 leading-relaxed pl-5 mb-4 space-y-2">
          <li>
            Tippe in der Safari-Leiste auf das{' '}
            <span className="inline-flex items-center align-middle">
              <ShareIcon />
            </span>{' '}
            Teilen-Symbol{safari ? ' unten' : ''}.
          </li>
          <li>
            Wähle <strong className="font-semibold">„Zum Home-Bildschirm&quot;</strong>{' '}
            (ggf. nach unten scrollen).
          </li>
          <li>
            Bestätige mit <strong className="font-semibold">„Hinzufügen&quot;</strong>{' '}
            oben rechts.
          </li>
        </ol>

        <button
          onClick={dismiss}
          className="w-full bg-emerald-500 text-white rounded-lg py-3 text-[13px] font-medium min-h-[44px] hover:bg-emerald-600 transition-colors cursor-pointer"
        >
          Verstanden
        </button>
      </>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(17,24,39,0.3)' }}
        onClick={handleLater}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={
          view === 'ios-guide'
            ? 'Zum Home-Bildschirm hinzufügen'
            : 'Als App speichern'
        }
        data-testid="install-prompt-sheet"
        className="relative bg-white rounded-t-[20px] p-6"
      >
        <div className="w-9 h-1 bg-gray-300 rounded-full mx-auto mb-4" />

        {view === 'prompt' ? renderPromptView() : renderIOSGuide()}

        <div style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
      </div>
    </div>,
    document.body
  );
};

const ShareIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 3v12" />
    <path d="m7 8 5-5 5 5" />
    <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
  </svg>
);

export default InstallPromptSheet;
